import { useTheme } from '@nexelem/deprecated-theme';
import { Status, StatusColors } from '@nexelem/components';
import { QueueStored } from '../store';
import { memo } from 'react';
import { QueueColumnWidthOptions, useProductionPlanSettings } from '../components/SettingsDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareXmark } from '@fortawesome/pro-solid-svg-icons';
import { clsx } from 'clsx';
import { faIndustryWindows } from '@fortawesome/pro-light-svg-icons';

export type QueueWithStatus = QueueStored & { status?: StatusColors };

export type GanttQueueLabelProps = Pick<QueueWithStatus, 'name' | 'shortName' | 'status'> & {
  areaHex: string;
  areaName: string;
  active: boolean;
};

export const GanttQueueLabel = memo(({ name, shortName, status, areaHex, areaName, active }: GanttQueueLabelProps) => {
  const {
    palette: { hexToRGB },
  } = useTheme();

  const columnWidth = useProductionPlanSettings((state) => state.gantt.queuesColumnWidth);

  const queueColor = `#${areaHex}`;

  return (
    <div className="box-border h-15.25 border-b border-b-border-secondary p-2 pl-4">
      {columnWidth === QueueColumnWidthOptions.SHORT && (
        <div
          className={clsx(
            'relative mt-1 flex h-7.5 w-24.5 items-center justify-center rounded-xs border',
            !active && 'bg-surface-primary',
          )}
          style={{
            backgroundColor: active ? hexToRGB(queueColor, 0.2) : undefined,
            borderColor: hexToRGB(queueColor, 0.4),
          }}
        >
          {!!status && (
            <div className="absolute right-0 bottom-0 translate-1/2">
              <Status color={status} variant="dot" />
            </div>
          )}
          {!active && (
            <FontAwesomeIcon
              className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 text-icon-darker [background:radial-gradient(at_center,white_40%,transparent_40%)]"
              icon={faSquareXmark}
            />
          )}
          <span className="text-s11-sb uppercase" style={{ color: queueColor }}>
            {shortName}
          </span>
        </div>
      )}
      {columnWidth === QueueColumnWidthOptions.WIDE && (
        <div className="flex items-center gap-2.5">
          <div
            className={clsx(
              'relative flex h-7 min-h-7 w-7 min-w-7 items-center justify-center rounded-xs border p-2',
              !active && 'bg-surface-primary',
            )}
            style={{
              color: queueColor,
              backgroundColor: active ? hexToRGB(queueColor, 0.2) : undefined,
              borderColor: hexToRGB(queueColor, 0.4),
            }}
          >
            <FontAwesomeIcon icon={faIndustryWindows} />
            {!!status && (
              <div className="absolute right-0 bottom-0 translate-1/2">
                <Status color={status} variant="dot" />
              </div>
            )}
            {!active && (
              <FontAwesomeIcon
                className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 text-icon-darker [background:radial-gradient(at_center,white_40%,transparent_40%)]"
                icon={faSquareXmark}
              />
            )}
          </div>
          <div className="flex max-w-40 flex-col">
            <span className="line-clamp-2 max-w-40 text-t14-sb text-text-primary">{name}</span>
            <span className="truncate text-s11-sb uppercase" style={{ color: queueColor }}>
              {areaName}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

GanttQueueLabel.displayName = 'GanttQueueLabel';
